import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Bot, X, Send, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WELCOME = `Dzień dobry! 👋 Tu wirtualny recepcjonista **Feniks Gastro AGD**.

Pomogę zebrać informacje o usterce i sprawdzić **najprostsze rzeczy**, ale finalną diagnozę zawsze wykonuje nasz serwisant na miejscu.

Proszę napisać **jakie urządzenie** i **co się dzieje** (np. "zmywarka Bosch nie startuje").`;

function getSessionId() {
  let sid = localStorage.getItem("feniks_chat_sid");
  if (!sid) {
    sid = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("feniks_chat_sid", sid);
  }
  return sid;
}

// Lightweight markdown -> safe HTML for bold (**text**) and bullet lists
function renderMessage(text) {
  const lines = text.split("\n");
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc list-inside space-y-1 my-1.5">
          {listBuf.map((li, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inline(li) }} />
          ))}
        </ul>
      );
      listBuf = [];
    }
  };
  const inline = (s) =>
    s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/`(.+?)`/g, '<code class="bg-zinc-800 text-emerald-300 px-1 rounded">$1</code>');
  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (/^[-*•]\s+/.test(trimmed)) {
      listBuf.push(trimmed.replace(/^[-*•]\s+/, ""));
    } else if (trimmed === "") {
      flushList();
      out.push(<div key={`br-${i}`} className="h-1" />);
    } else {
      flushList();
      out.push(<p key={`p-${i}`} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: inline(line) }} />);
    }
  });
  flushList();
  return out;
}

export default function AIChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: WELCOME }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const sid = useRef(getSessionId());

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sid.current, message: text });
      setMessages((m) => [...m, { role: "assistant", text: res.data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "Przepraszam, wystąpił błąd. Spróbuj ponownie za chwilę albo wypełnij formularz kontaktowy." },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          data-testid="chat-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 left-6 md:bottom-8 md:left-8 z-50 group flex items-center gap-3 bg-zinc-950 hover:bg-[var(--feniks-red)] text-white pl-4 pr-5 py-3 shadow-xl transition-colors"
        >
          <span className="relative inline-flex w-8 h-8 items-center justify-center bg-[var(--feniks-red)] group-hover:bg-white group-hover:text-[var(--feniks-red)] transition-colors">
            <Bot size={18} />
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
          </span>
          <span className="text-xs font-bold uppercase tracking-widest">Asystent AI</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed inset-x-0 bottom-0 sm:bottom-6 sm:left-6 md:bottom-8 md:left-8 sm:inset-x-auto z-50 w-full sm:w-[400px] md:w-[440px] h-[80vh] sm:h-[600px] bg-zinc-950 text-zinc-100 border-t sm:border border-zinc-800 shadow-2xl flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-900">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 flex items-center justify-center bg-[var(--feniks-red)]">
                <Bot size={18} />
              </div>
              <div className="min-w-0">
                <div className="font-display text-sm tracking-tight">Asystent diagnostyczny</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-400 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Online · AI
                </div>
              </div>
            </div>
            <button
              data-testid="chat-close-btn"
              onClick={() => setOpen(false)}
              className="p-1.5 hover:bg-zinc-800 transition-colors"
              aria-label="zamknij czat"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chat-messages"
            className="flex-1 overflow-y-auto feniks-scroll px-4 py-4 space-y-3 font-mono text-[13px]"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-msg-${i}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[90%] px-3.5 py-2.5 ${
                    m.role === "user"
                      ? "bg-[var(--feniks-red)] text-white font-sans"
                      : "bg-zinc-900 border border-zinc-800 text-zinc-100"
                  }`}
                >
                  {m.role === "user" ? m.text : <div className="space-y-1">{renderMessage(m.text)}</div>}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-zinc-900 border border-zinc-800 px-3.5 py-2.5 flex items-center gap-2 text-zinc-400 text-xs">
                  <Loader2 size={14} className="animate-spin" /> myślę...
                </div>
              </div>
            )}
          </div>

          {/* Footer disclaimer */}
          <div className="px-4 py-2 border-t border-zinc-800 flex items-center gap-2 text-[10px] text-zinc-500 font-mono">
            <Sparkles size={11} className="text-[var(--feniks-red)]" />
            Odpowiedzi AI mają charakter pomocniczy. Diagnoza końcowa = serwisant.
          </div>

          {/* Input */}
          <form onSubmit={send} className="border-t border-zinc-800 p-3 flex items-end gap-2 bg-zinc-900">
            <textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              placeholder="Np. Bosch WAB28, kod E18..."
              className="flex-1 bg-zinc-950 border border-zinc-800 px-3 py-2.5 text-sm text-zinc-100 focus:border-[var(--feniks-red)] focus:ring-1 focus:ring-[var(--feniks-red)] outline-none resize-none max-h-32"
            />
            <button
              data-testid="chat-send-btn"
              type="submit"
              disabled={!input.trim() || sending}
              className="shrink-0 bg-[var(--feniks-red)] hover:bg-[var(--feniks-red-hover)] disabled:opacity-50 disabled:cursor-not-allowed text-white p-2.5 transition-colors"
              aria-label="wyślij"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
